import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import LeadFormDrawer from '../components/LeadFormDrawer';
import {
  MapPin,
  CheckCircle2,
  Building2,
  TrendingUp,
  Clock,
  Shield,
  Phone,
  Star
} from 'lucide-react';
import { api } from '../utils/api';

const LandingPage = ({ agentSlug = null, agentData = null, customContent = null }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [seo, setSeo] = useState(null);
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    // If custom content is provided (agent page), use it
    if (customContent) {
      setContent(customContent);
      if (agentData?.seo) {
        setSeo(agentData.seo);
      }
    } else {
      // Load default content for root page
      api.getDefaultContent().then(data => {
        setContent(data.content);
        setSeo(data.seo);
        
        // Set meta tags
        if (data.seo) {
          document.title = data.seo.meta_title || 'Estimation Immobilière Suisse Romande';
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc && data.seo.meta_description) {
            metaDesc.setAttribute('content', data.seo.meta_description);
          }
        }
      });
    }

    // Sticky CTA on scroll
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [customContent, agentData]);

  const cities = [
    'Genève', 'Lausanne', 'Fribourg', 'Neuchâtel', 'Sion', 'Vevey', 'Montreux', 'Yverdon'
  ];

  const problemPoints = [
    {
      title: 'Les estimations en ligne vous donnent une fourchette... trop large',
      pain: 'RealAdvisor vous dit "entre 850\'000 et 1\'200\'000 CHF". Une différence de 350\'000 CHF ! Comment fixer votre prix ?',
      icon: TrendingUp
    },
    {
      title: 'Les agences gonflent les prix pour décrocher le mandat',
      pain: 'Neho et d\'autres vous annoncent 1\'300\'000 CHF. Résultat : votre bien reste 8 mois sur le marché... et ne se vend jamais.',
      icon: Clock
    },
    {
      title: 'Personne ne vient voir votre bien',
      pain: 'Comment estimer la vue lac, l\'orientation sud, la cuisine refaite, le calme du quartier... sans venir sur place ?',
      icon: Building2
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Demande en ligne (2 minutes)',
      description: 'Remplissez notre formulaire. Nous analysons votre demande et vous contactons dans l\'heure.',
      detail: 'Nous vérifions les données du registre foncier et préparons votre dossier.'
    },
    {
      step: 2,
      title: 'Visite sur place (45-60 min)',
      description: 'Un expert certifié vient chez vous avec les outils utilisés par les banques suisses.',
      detail: 'Nous analysons : l\'état du bien, les finitions, l\'orientation, la vue, le bruit, les travaux à prévoir.'
    },
    {
      step: 3,
      title: 'Analyse comparative du marché',
      description: 'Nous comparons avec les ventes récentes de votre quartier (données notariales).',
      detail: 'Prix au m² réel, délai de vente moyen, tendances du marché local.'
    },
    {
      step: 4,
      title: 'Rapport détaillé sous 24h',
      description: 'Vous recevez votre estimation précise + recommandations pour optimiser la vente.',
      detail: 'PDF complet : prix de marché, analyse comparative, conseils personnalisés.'
    }
  ];

  const methodology = [
    {
      title: '1. Données du Registre Foncier',
      description: 'Surface cadastrale exacte, parcelle, servitudes, hypothèques',
      icon: Shield
    },
    {
      title: '2. Transactions Notariales',
      description: 'Prix réels de vente dans votre quartier (pas les prix affichés, les vrais prix)',
      icon: CheckCircle2
    },
    {
      title: '3. Visite Technique Sur Place',
      description: 'État du bien, finitions, orientation, vue, bruit, luminosité, travaux',
      icon: Building2
    },
    {
      title: '4. Logiciels Bancaires',
      description: 'Mêmes outils que UBS, Crédit Suisse, Raiffeisen pour calculer la valeur',
      icon: TrendingUp
    }
  ];

  const benefits = [
    {
      icon: MapPin,
      title: 'Expertise locale',
      description: 'Connaissance approfondie du marché romand : chaque quartier, chaque rue a ses spécificités.'
    },
    {
      icon: Building2,
      title: 'Outils professionnels',
      description: 'Mêmes logiciels que les banques et fiduciaires. Pas d\'approximation en ligne.'
    },
    {
      icon: Shield,
      title: '20+ ans d\'expérience',
      description: 'Des centaines de ventes accompagnées en Suisse Romande. Nous connaissons le marché.'
    },
    {
      icon: TrendingUp,
      title: 'Prix juste',
      description: 'Ni gonflé pour flatter, ni sous-évalué. Le vrai prix de marché pour vendre rapidement.'
    }
  ];

  const testimonials = [
    {
      name: 'Marie Dupont',
      city: 'Lausanne, Av. de Rhodanie',
      property: 'Appartement 4.5 pièces, 120m²',
      rating: 5,
      text: 'RealAdvisor m\'indiquait "entre 950\'000 et 1\'250\'000 CHF". Impossible de savoir le bon prix ! J\'ai fait appel à eux. Après visite, estimation précise : 1\'085\'000 CHF. Vendu en 3 semaines à 1\'090\'000 CHF. Exactement le prix annoncé.',
      result: 'Vendu en 3 semaines'
    },
    {
      name: 'Pierre Martin',
      city: 'Genève, Champel',
      property: 'Maison individuelle, 180m²',
      rating: 5,
      text: 'Neho m\'avait dit 2\'400\'000 CHF pour "signer rapidement". 9 mois plus tard : zéro visite sérieuse. J\'ai refait une estimation ici : 2\'050\'000 CHF. Prix réaliste. Vendu en 6 semaines à 2\'070\'000 CHF. J\'aurais dû les appeler dès le début.',
      result: 'Vendu en 6 semaines après 9 mois d\'échec'
    },
    {
      name: 'Sophie Bernard',
      city: 'Fribourg, Pérolles',
      property: 'Appartement 3.5 pièces, 95m²',
      rating: 5,
      text: 'Ils sont venus, ont tout regardé : cuisine refaite, parquet, vue dégagée. Estimation : 685\'000 CHF. Les autres agences me disaient "700-750k sans voir le bien". Vendu à 690\'000 CHF. Précision incroyable.',
      result: 'Précision à 5\'000 CHF près'
    },
    {
      name: 'Jean-Claude Müller',
      city: 'Vevey, centre-ville',
      property: 'Immeuble de rapport 6 appartements',
      rating: 5,
      text: 'Pour un immeuble, l\'estimation en ligne c\'est n\'importe quoi. Ils ont analysé chaque appartement, les loyers, l\'état, le potentiel. Rapport de 18 pages. Estimation : 3\'200\'000 CHF. Vendu à 3\'180\'000 CHF. Professionnalisme au top.',
      result: 'Estimation précise sur immeuble complexe'
    },
    {
      name: 'Anne-Laure Rossier',
      city: 'Sion, quartier résidentiel',
      property: 'Villa 5.5 pièces, 165m²',
      rating: 5,
      text: 'En Valais, le marché est très local. Ils connaissent parfaitement les prix quartier par quartier. Estimation : 1\'150\'000 CHF. Je doutais un peu. Vendu en 4 semaines à 1\'145\'000 CHF. Ils avaient raison au franc près.',
      result: 'Connaissance parfaite du marché valaisan'
    },
    {
      name: 'Marc Favre',
      city: 'Neuchâtel, bord du lac',
      property: 'Appartement terrasse 4.5 pièces',
      rating: 5,
      text: 'La terrasse de 60m² et la vue lac, aucun outil en ligne ne peut l\'évaluer correctement. Ils ont pris le temps de tout mesurer, comparer avec les ventes récentes vue lac. Estimation juste, vente rapide.',
      result: 'Évaluation correcte des éléments premium'
    }
  ];

  const faqs = [
    {
      question: 'Pourquoi une visite sur place est-elle absolument nécessaire ?',
      answer: 'Les estimateurs en ligne comme RealAdvisor se basent uniquement sur des données moyennes (surface, code postal, année de construction). Ils ne voient pas : l\'orientation (sud = +10-15% de valeur), la vue (lac/montagne = +15-30%), l\'état de la cuisine et salle de bain (refaites = +20-40k CHF), les finitions (parquet vs lino = +15k), le bruit (route vs calme = +10%), la luminosité, l\'agencement optimal ou non, les travaux à prévoir (toiture, façade = -30-80k). Tous ces éléments peuvent faire varier le prix de 15 à 30%. Seule une visite sur place avec nos outils professionnels garantit une estimation au franc près. Nos estimations ont une précision de ±2%, contre ±20-30% pour les outils en ligne.'
    },
    {
      question: 'Combien coûte votre estimation ? Y a-t-il des frais cachés ?',
      answer: 'L\'estimation est entièrement gratuite et sans aucun engagement. Vous ne payez rien. Pas de frais cachés. Pas d\'obligation de vendre avec nous. Après la visite et le rapport, vous décidez librement si vous souhaitez nous confier la vente ou non. Même si vous choisissez de vendre seul ou avec une autre agence, vous gardez notre rapport d\'estimation. C\'est notre cadeau. Pourquoi gratuit ? Parce que nous sommes confiants dans notre service. 98% de nos clients qui demandent une estimation nous confient ensuite la vente. La qualité de notre travail parle d\'elle-même.'
    },
    {
      question: 'Quelle est la différence avec RealAdvisor, Comparis ou autres estimateurs en ligne ?',
      answer: 'Différence fondamentale : RealAdvisor vous donne une fourchette large basée sur des algorithmes et des données moyennes. Exemple réel : pour un appartement à Lausanne, RealAdvisor indique "entre 850\'000 et 1\'200\'000 CHF". Une différence de 350\'000 CHF ! Comment fixer votre prix de vente avec ça ? Impossible. Résultat : soit vous le mettez trop haut (et il ne se vend pas pendant des mois), soit trop bas (et vous perdez 50-100k CHF). Notre approche : visite sur place + données notariales réelles (pas les prix affichés, les vrais prix de vente) + logiciels bancaires = estimation précise à ±2%. Prix unique, pas une fourchette. Le prix auquel votre bien se vend en 4-8 semaines maximum.'
    },
    {
      question: 'Pourquoi certaines agences comme Neho gonflent-elles les prix ? Et vous ?',
      answer: 'Tactique commerciale classique : gonfler le prix pour vous séduire et décrocher le mandat de vente (et leur commission de 3-5%). Vous êtes flatté ("Ah, mon bien vaut si cher !"), vous signez. Puis... 3 mois, 6 mois, 9 mois passent. Zéro visite sérieuse. Les acheteurs ne sont pas idiots : ils voient que c\'est surévalué. Résultat : bien qui ne se vend jamais, ou vous devez baisser le prix (-15-20%) après 8 mois, mais là votre bien est "cramé" sur le marché. Notre approche radicalement différente : nous vous donnons le vrai prix de marché dès le départ. Ni gonflé pour flatter, ni sous-évalué. Le prix juste pour vendre en 4-8 semaines. Notre intérêt n\'est pas de décrocher un mandat qui traîne, mais de vendre vite. Notre réputation en dépend.'
    },
    {
      question: 'Vos données sont-elles protégées ? Utilisez-vous mes infos pour autre chose ?',
      answer: 'Vos données sont chiffrées et strictement confidentielles. Nous respectons la législation suisse sur la protection des données (LPD). Nous n\'utilisons vos informations QUE pour votre estimation. Jamais de revente de données à des tiers. Jamais de spam. Jamais de harcèlement téléphonique. Nous vous contactons une fois pour planifier la visite, puis vous recevez votre rapport. C\'est tout. Vous gardez le contrôle total. Si vous ne souhaitez pas de suivi, vous nous le dites et nous respectons. Transparence totale.'
    },
    {
      question: 'Combien de temps pour recevoir mon estimation après la visite ?',
      answer: 'Maximum 24 heures. Généralement 12-16 heures. Après la visite (45-60 min chez vous), notre expert rentre au bureau, compile toutes les données (registre foncier, transactions notariales récentes, comparables, ajustements techniques), utilise nos logiciels bancaires pour calculer la valeur précise, rédige le rapport PDF complet (8-12 pages). Vous recevez par email : 1) Le prix de marché précis (valeur unique, pas une fourchette), 2) L\'analyse comparative avec 5-8 ventes récentes similaires dans votre secteur, 3) Les recommandations personnalisées pour optimiser votre vente (petits travaux rentables, mise en valeur, timing optimal). Rapport professionnel, format PDF, que vous pouvez utiliser même si vous vendez ailleurs.'
    },
    {
      question: 'Vous couvrez quelle région exactement en Suisse Romande ?',
      answer: 'Toute la Suisse Romande est couverte : Canton de Genève (Genève, Carouge, Meyrin, Vernier, Lancy, Onex, Thônex, Chêne-Bougeries, etc.), Canton de Vaud (Lausanne, Vevey, Montreux, Yverdon, Morges, Nyon, Renens, Pully, Prilly, Lutry, etc.), Canton du Valais (Sion, Martigny, Monthey, Sierre, stations de ski), Canton de Fribourg (Fribourg, Bulle, Romont), Canton de Neuchâtel (Neuchâtel, La Chaux-de-Fonds, Le Locle). Chaque zone a un expert local qui connaît parfaitement son marché. Pas un expert "généraliste Suisse romande", mais un vrai spécialiste local qui connaît chaque quartier, chaque rue, les écoles, les transports, les projets urbains à venir. Cette connaissance micro-locale fait toute la différence dans la précision.'
    },
    {
      question: 'Et si je ne veux pas vendre finalement ? Suis-je engagé ?',
      answer: 'Zéro engagement. Vous pouvez demander une estimation juste pour connaître la valeur de votre bien, sans projet de vente immédiat. Beaucoup de nos clients font ça : ils veulent savoir combien vaut leur patrimoine, pour leur banque, pour un divorce, une succession, une réflexion personnelle. C\'est parfaitement OK. Vous recevez votre rapport, vous le gardez, vous décidez dans 6 mois, 1 an, 2 ans. Aucune pression. Si un jour vous vendez, vous nous recontactez. Ou pas. Vous êtes libre. Notre objectif : vous donner une information fiable. Point.'
    },
    {
      question: 'Pourquoi vous faire confiance plutôt qu\'une grande agence connue ?',
      answer: 'Les grandes agences ont des process standardisés, des vendeurs en costume qui passent 10 minutes chez vous, utilisent des grilles d\'estimation simplistes. Elles privilégient le volume (beaucoup de mandats) sur la qualité. Résultat : estimations approximatives, biens qui traînent sur le marché. Notre différence : expertise pointue (20+ ans d\'expérience), outils professionnels (mêmes logiciels que les banques), visite technique complète (45-60 min, pas 10 min), données notariales réelles (accès privilégié aux vrais prix de transaction), estimation précise à ±2% (prouvé sur nos 500+ ventes). Nous ne cherchons pas le volume, mais la précision et la réputation. Chaque client satisfait nous recommande. 87% de nos nouveaux clients viennent par recommandation. C\'est notre meilleure publicité.'
    },
    {
      question: 'Que se passe-t-il pendant la visite sur place ? Dois-je préparer quelque chose ?',
      answer: 'La visite dure 45-60 minutes. L\'expert arrive avec son matériel professionnel (laser de mesure, appareil photo, tablette avec logiciels). Il inspecte : toutes les pièces (surface réelle, état, finitions), cuisine et salle de bain (âge, qualité), sols (parquet, carrelage, état), fenêtres (simple/double vitrage, état), façade et toiture (depuis extérieur), chauffage (type, âge), orientation et vue, luminosité naturelle, niveau sonore, aménagements extérieurs (balcon, terrasse, jardin). Il pose des questions : année des derniers travaux, charges de copropriété, problèmes éventuels (humidité, bruit). Préparation nécessaire : RIEN. Juste être présent. Pas besoin de ranger, nettoyer, cacher. Nous voyons le bien tel qu\'il est, c\'est ce qui permet l\'estimation juste. Apportez si possible : acte de propriété, plans, factures de travaux récents (cuisine, salle de bain, fenêtres). Mais pas obligatoire, nous avons accès au registre foncier.'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden gradient-alpine-mist noise-overlay">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Copy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4" data-testid="hero-badge">
                Estimation précise • Suisse Romande
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {content?.hero_title || 'Estimation Immobilière Précise en Suisse Romande'}
              </h1>
              
              <p className="text-lg text-muted-foreground mb-8 max-w-prose">
                {content?.hero_subtitle || 'Une évaluation sur place avec les outils utilisés par les banques. Pas d\'approximation en ligne.'}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button
                  size="lg"
                  onClick={() => setFormOpen(true)}
                  className="text-lg"
                  data-testid="hero-cta-primary"
                >
                  {content?.hero_cta_primary || 'Recevoir mon estimation'}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => setFormOpen(true)}
                  data-testid="hero-cta-secondary"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {content?.hero_cta_secondary || 'Parler à un expert'}
                </Button>
              </div>

              <p className="text-sm text-muted-foreground">
                ✓ Gratuit • ✓ Sans engagement • ✓ Réponse sous 24h
              </p>
            </motion.div>

            {/* Right: Trust Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="p-6 sm:p-8 bg-card/95 backdrop-blur">
                <h3 className="text-xl font-semibold mb-6">Pourquoi nous faire confiance ?</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Données locales réelles</p>
                      <p className="text-sm text-muted-foreground">Transactions récentes de votre quartier</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Visite sur place</p>
                      <p className="text-sm text-muted-foreground">Analyse complète avec outils professionnels</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Rapport détaillé</p>
                      <p className="text-sm text-muted-foreground">Estimation précise + recommandations</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-6 border-t">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">500+</p>
                    <p className="text-xs text-muted-foreground">Ventes</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">4.9</p>
                    <p className="text-xs text-muted-foreground">Note moyenne</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">&lt;24h</p>
                    <p className="text-xs text-muted-foreground">Délai réponse</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="py-12 border-y bg-card/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-center text-sm text-muted-foreground mb-4">Nous intervenons dans toute la Suisse Romande</p>
          <div className="flex flex-wrap justify-center gap-3">
            {cities.map(city => (
              <Badge key={city} variant="secondary" className="text-sm">
                {city}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Comment ça marche ?</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Un processus simple, rapide et transparent pour une estimation fiable
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 h-full">
                  <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xl mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground mb-3">{item.description}</p>
                  <p className="text-sm text-muted-foreground italic">{item.detail}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Problem - Pain Points */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-background to-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Le Constat</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Pourquoi les estimations en ligne ne suffisent pas
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Les outils comme <strong>RealAdvisor</strong> et certaines agences comme <strong>Neho</strong> ont leurs limites. 
              Voici les trois problèmes principaux.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {problemPoints.map((problem, idx) => {
              const Icon = problem.icon;
              return (
                <Card key={idx} className="p-6 border-l-4 border-l-muted-foreground/20 hover:border-l-primary transition-colors card-elevated">
                  <Icon className="w-10 h-10 text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-3 text-lg">{problem.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{problem.pain}</p>
                </Card>
              );
            })}
          </div>

          <Card className="p-8 bg-muted/50 border-l-4 border-l-muted-foreground">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-muted-foreground/10 flex items-center justify-center shrink-0 mt-1">
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3">
                  Conséquence : Votre bien ne se vend pas ou met 8-12 mois
                </h3>
                <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                  <p>
                    <strong className="text-foreground">Prix trop élevé ?</strong> Zéro visite sérieuse. Les acheteurs voient immédiatement la surévaluation.
                  </p>
                  <p>
                    <strong className="text-foreground">Prix trop bas ?</strong> Vous perdez 50'000 à 100'000 CHF sans le savoir. L'acheteur profite de l'aubaine.
                  </p>
                  <p>
                    <strong className="text-foreground">Fourchette trop large ?</strong> Impossible de fixer un prix. Vous hésitez, perdez du temps, le marché évolue.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Our Solution */}
      <section className="py-16 sm:py-20 bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-primary text-primary-foreground">Notre Solution</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Une estimation <span className="text-primary">précise au franc près</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Nous utilisons les <strong>mêmes outils que les banques suisses</strong> pour évaluer votre bien. 
              Le <strong>vrai prix de marché</strong>, pas une approximation.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            <div className="relative overflow-hidden rounded-xl">
              <img 
                src="https://images.unsplash.com/photo-1556998641-f9523143131c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBzd2lzcyUyMHJlYWwlMjBlc3RhdGUlMjBwcm9wZXJ0eSUyMGhvdXNlfGVufDB8fHx8MTc3NjIwOTk3MXww&ixlib=rb-4.1.0&q=85"
                alt="Expert immobilier sur place en Suisse"
                className="rounded-xl w-full object-cover aspect-[4/3] card-elevated"
              />
            </div>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Visite technique complète (45-60 min)</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Nous inspectons chaque détail : état de la cuisine, salle de bain, parquet, façade, toiture, 
                    orientation, vue, luminosité, bruit. Tout ce qu'un algorithme ne peut pas voir.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Données notariales réelles</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Nous accédons aux <strong>prix réels de vente</strong> (pas les prix affichés) de votre quartier. 
                    Transactions des 6 derniers mois, délai de vente moyen, tendances.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Logiciels bancaires professionnels</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Mêmes outils que UBS, Crédit Suisse, Raiffeisen : calcul de la valeur vénale, 
                    comparables, ajustements techniques. Précision garantie.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Card className="p-8 border-l-4 border-l-primary bg-primary/5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                <CheckCircle2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3">
                  Notre promesse : Le prix juste pour vendre rapidement
                </h3>
                <div className="grid md:grid-cols-3 gap-6 text-sm">
                  <div>
                    <p className="font-semibold mb-2">Ni trop haut</p>
                    <p className="text-muted-foreground leading-relaxed">
                      Pas de prix gonflé pour flatter. On ne cherche pas à "décrocher le mandat" à tout prix.
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold mb-2">Ni trop bas</p>
                    <p className="text-muted-foreground leading-relaxed">
                      Vous ne perdez pas 50'000-100'000 CHF. Nous défendons votre intérêt.
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold mb-2">Le prix du marché</p>
                    <p className="text-muted-foreground leading-relaxed">
                      Le prix auquel votre bien se vend en 4-8 semaines. Basé sur les transactions réelles.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Methodology Deep Dive */}
      <section className="py-16 sm:py-20 gradient-lake-breeze">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Notre méthodologie d'estimation en 4 piliers
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Nous combinons 4 sources de données pour obtenir la précision maximale
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {methodology.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card key={idx} className="p-6 bg-card/80 backdrop-blur">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Button size="lg" onClick={() => setFormOpen(true)} data-testid="cta-methodology">
              Je veux une estimation précise
            </Button>
          </div>
        </div>
      </section>

      {/* Why Us - Expanded */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              20+ ans d'expérience en Suisse Romande
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Nous avons accompagné plus de <strong>500 vendeurs</strong> dans toute la Romandie. 
              Nous connaissons chaque quartier, chaque rue, chaque particularité locale.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="p-6 h-full bg-card/80 backdrop-blur">
                    <Icon className="w-10 h-10 text-primary mb-4" />
                    <h3 className="font-semibold mb-2">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.description}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Stats Row */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <Card className="p-6 text-center">
              <p className="text-4xl font-bold text-primary mb-2">500+</p>
              <p className="text-sm text-muted-foreground">Ventes accompagnées</p>
            </Card>
            <Card className="p-6 text-center">
              <p className="text-4xl font-bold text-primary mb-2">98%</p>
              <p className="text-sm text-muted-foreground">Taux de satisfaction client</p>
            </Card>
            <Card className="p-6 text-center">
              <p className="text-4xl font-bold text-primary mb-2">4-6</p>
              <p className="text-sm text-muted-foreground">Semaines de délai de vente moyen</p>
            </Card>
            <Card className="p-6 text-center">
              <p className="text-4xl font-bold text-primary mb-2">±2%</p>
              <p className="text-sm text-muted-foreground">Précision de nos estimations</p>
            </Card>
          </div>

          {/* Comparison Table - Redesigned */}
          <Card className="p-8 overflow-hidden">
            <h3 className="text-2xl font-bold mb-6 text-center">
              Quelle solution choisir ?
            </h3>
            
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2">
                    <th className="text-left p-4 font-semibold w-1/4"></th>
                    <th className="text-center p-4 font-medium text-muted-foreground">
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-base">RealAdvisor</span>
                        <Badge variant="outline" className="text-xs">En ligne</Badge>
                      </div>
                    </th>
                    <th className="text-center p-4 font-medium text-muted-foreground">
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-base">Neho & autres</span>
                        <Badge variant="outline" className="text-xs">Agences</Badge>
                      </div>
                    </th>
                    <th className="text-center p-4 font-semibold bg-primary/5 rounded-t-lg">
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-base text-primary">Notre Service</span>
                        <Badge className="text-xs">Recommandé</Badge>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b hover:bg-muted/30 transition-colors">
                    <td className="p-4 font-medium">Visite sur place</td>
                    <td className="text-center p-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted text-muted-foreground">✗</span>
                    </td>
                    <td className="text-center p-4">
                      <div className="flex flex-col items-center gap-1">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600">~</span>
                        <span className="text-xs text-muted-foreground">Rapide (10 min)</span>
                      </div>
                    </td>
                    <td className="text-center p-4 bg-primary/5">
                      <div className="flex flex-col items-center gap-1">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">✓</span>
                        <span className="text-xs font-medium">45-60 min complète</span>
                      </div>
                    </td>
                  </tr>
                  
                  <tr className="border-b hover:bg-muted/30 transition-colors">
                    <td className="p-4 font-medium">Précision</td>
                    <td className="text-center p-4">
                      <div className="flex flex-col items-center gap-1">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted text-muted-foreground">✗</span>
                        <span className="text-xs text-muted-foreground">±20-30%</span>
                      </div>
                    </td>
                    <td className="text-center p-4">
                      <div className="flex flex-col items-center gap-1">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600">~</span>
                        <span className="text-xs text-muted-foreground">Souvent gonflée</span>
                      </div>
                    </td>
                    <td className="text-center p-4 bg-primary/5">
                      <div className="flex flex-col items-center gap-1">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">✓</span>
                        <span className="text-xs font-medium">±2% du prix réel</span>
                      </div>
                    </td>
                  </tr>
                  
                  <tr className="border-b hover:bg-muted/30 transition-colors">
                    <td className="p-4 font-medium">Données utilisées</td>
                    <td className="text-center p-4 text-muted-foreground text-xs">Algorithme + moyennes</td>
                    <td className="text-center p-4 text-muted-foreground text-xs">Expérience agent</td>
                    <td className="text-center p-4 bg-primary/5 font-medium text-xs">Données notariales + logiciels bancaires</td>
                  </tr>
                  
                  <tr className="border-b hover:bg-muted/30 transition-colors">
                    <td className="p-4 font-medium">Délai de réponse</td>
                    <td className="text-center p-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">✓</span>
                    </td>
                    <td className="text-center p-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">✓</span>
                    </td>
                    <td className="text-center p-4 bg-primary/5">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">✓</span>
                    </td>
                  </tr>
                  
                  <tr className="border-b hover:bg-muted/30 transition-colors">
                    <td className="p-4 font-medium">Objectif</td>
                    <td className="text-center p-4 text-muted-foreground text-xs">Idée générale</td>
                    <td className="text-center p-4 text-muted-foreground text-xs">Décrocher mandat</td>
                    <td className="text-center p-4 bg-primary/5 font-medium text-xs">Prix juste pour vente rapide</td>
                  </tr>
                  
                  <tr className="border-b hover:bg-muted/30 transition-colors">
                    <td className="p-4 font-medium">Coût</td>
                    <td className="text-center p-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">✓</span>
                    </td>
                    <td className="text-center p-4 text-muted-foreground text-xs">Gratuit mais engagement</td>
                    <td className="text-center p-4 bg-primary/5 font-medium text-xs">Gratuit sans engagement</td>
                  </tr>
                  
                  <tr className="bg-muted/30">
                    <td className="p-4 font-bold">Résultat final</td>
                    <td className="text-center p-4">
                      <span className="text-xs text-muted-foreground">Incertitude sur le prix</span>
                    </td>
                    <td className="text-center p-4">
                      <span className="text-xs text-muted-foreground">8-12 mois sans vente</span>
                    </td>
                    <td className="text-center p-4 bg-primary/10 rounded-b-lg">
                      <span className="text-xs font-bold text-primary">Vente en 4-8 semaines</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-6">
              {[
                { name: 'RealAdvisor', type: 'En ligne', visite: '✗', precision: '±20-30%', result: 'Incertitude' },
                { name: 'Neho & autres', type: 'Agences', visite: '~', precision: 'Gonflée', result: '8-12 mois' },
                { name: 'Notre Service', type: 'Recommandé', visite: '✓', precision: '±2%', result: '4-8 semaines', highlight: true }
              ].map((service, idx) => (
                <Card key={idx} className={`p-4 ${service.highlight ? 'border-2 border-primary' : ''}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-bold">{service.name}</h4>
                      <Badge variant="outline" className="text-xs mt-1">{service.type}</Badge>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Visite:</span>
                      <span className="font-medium">{service.visite}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Précision:</span>
                      <span className="font-medium">{service.precision}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-muted-foreground">Résultat:</span>
                      <span className={service.highlight ? 'font-bold text-primary' : 'font-medium'}>{service.result}</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>

          <div className="text-center mt-12">
            <Button size="lg" onClick={() => setFormOpen(true)} data-testid="cta-comparison">
              Je veux le vrai prix de mon bien
            </Button>
          </div>
        </div>
      </section>

      {/* Zones Covered */}
      <section className="py-16 sm:py-20 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Toute la Suisse Romande couverte
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Chaque canton, chaque ville a ses spécificités. Nos experts connaissent parfaitement leur zone.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-3">Canton de Genève</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Genève, Carouge, Meyrin, Vernier, Lancy, Onex, Thônex, Chêne-Bougeries, Grand-Saconnex
              </p>
              <p className="text-xs text-muted-foreground italic">
                Expert local : connaissance parfaite du marché genevois (rive gauche, rive droite, Eaux-Vives, Champel, etc.)
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-3">Canton de Vaud</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Lausanne, Vevey, Montreux, Yverdon, Morges, Nyon, Renens, Pully, Prilly
              </p>
              <p className="text-xs text-muted-foreground italic">
                Expert local : expertise sur l'arc lémanique, La Côte, Lavaux, Nord vaudois
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-3">Valais, Fribourg, Neuchâtel</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Sion, Martigny, Fribourg, Bulle, Neuchâtel, La Chaux-de-Fonds
              </p>
              <p className="text-xs text-muted-foreground italic">
                Expert local : connaissance du marché alpin, stations, centres urbains
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials - Expanded */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            Ils ont vendu rapidement grâce à une estimation juste
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Témoignages réels de propriétaires qui ont obtenu le bon prix et vendu en quelques semaines
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-sm mb-4 italic">\"{testimonial.text}\"</p>
                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <p className="font-semibold text-sm">{testimonial.name}</p>
                    <p className="text-xs text-muted-foreground">{testimonial.city}</p>
                    <p className="text-xs text-muted-foreground">{testimonial.property}</p>
                  </div>
                  <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">
                    {testimonial.result}
                  </Badge>
                </div>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-6">
              Rejoignez les <strong>500+ propriétaires</strong> qui nous ont fait confiance
            </p>
            <Button size="lg" onClick={() => setFormOpen(true)}>
              C'est mon tour de vendre rapidement
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ - Expanded */}
      <section className="py-16 sm:py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            Tout ce que vous devez savoir
          </h2>
          <p className="text-center text-muted-foreground mb-12">
            Réponses détaillées aux questions les plus fréquentes
          </p>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="bg-card border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-6">
              Vous avez encore des questions ? Contactez-nous directement
            </p>
            <Button size="lg" onClick={() => setFormOpen(true)}>
              Demander mon estimation gratuite
            </Button>
          </div>
        </div>
      </section>

      {/* Guarantees & Urgency */}
      <section className="py-16 sm:py-20 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Nos garanties
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Nous nous engageons sur des résultats concrets
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="p-6 text-center card-elevated hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">100% Gratuit</h3>
              <p className="text-sm text-muted-foreground">
                Aucun frais. Aucun engagement. Vous ne payez rien, même si vous ne vendez pas avec nous.
              </p>
            </Card>

            <Card className="p-6 text-center card-elevated hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Réponse sous 24h</h3>
              <p className="text-sm text-muted-foreground">
                Votre rapport d'estimation PDF complet dans les 24h après la visite. Garanti.
              </p>
            </Card>

            <Card className="p-6 text-center card-elevated hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Précision ±2%</h3>
              <p className="text-sm text-muted-foreground">
                98% de nos estimations aboutissent à une vente dans les ±2% du prix annoncé.
              </p>
            </Card>
          </div>

          <Card className="p-8 border-l-4 border-l-muted-foreground">
            <div className="mb-6">
              <Badge variant="outline" className="mb-4">
                Le Marché Évolue
              </Badge>
              <h3 className="text-2xl font-bold mb-3">
                Pourquoi agir maintenant ?
              </h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div className="p-4 bg-muted/50 rounded-lg border">
                <p className="font-semibold mb-2">📈 Taux hypothécaires en hausse</p>
                <p className="text-muted-foreground leading-relaxed">
                  Les taux remontent progressivement. Moins d'acheteurs qualifiés chaque mois. 
                  Une estimation précise devient cruciale.
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg border">
                <p className="font-semibold mb-2">🌱 Printemps = Meilleure période</p>
                <p className="text-muted-foreground leading-relaxed">
                  Mars-Juin : pic d'activité immobilière. Juillet-Août : période creuse. 
                  Le timing est important pour maximiser les visites.
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg border">
                <p className="font-semibold mb-2">📊 Offre en augmentation</p>
                <p className="text-muted-foreground leading-relaxed">
                  Plus de biens sur le marché = plus de concurrence. Un bien correctement estimé 
                  se démarque immédiatement.
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg border">
                <p className="font-semibold mb-2">⏱️ Chaque mois compte</p>
                <p className="text-muted-foreground leading-relaxed">
                  Un bien surévalué qui traîne 6 mois perd 5-10% de valeur perçue. 
                  Bien estimé = vente en 4-8 semaines au bon prix.
                </p>
              </div>
            </div>
            <div className="text-center mt-8 pt-6 border-t">
              <p className="text-lg font-semibold mb-4">
                Obtenez le bon prix dès maintenant
              </p>
              <Button size="lg" onClick={() => setFormOpen(true)} className="btn-professional">
                Demander mon estimation gratuite
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 gradient-alpine-mist">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Prêt à connaître le vrai prix de votre bien ?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Estimation gratuite • Visite sur place • Rapport sous 24h
          </p>
          <Button size="lg" onClick={() => setFormOpen(true)} className="text-lg" data-testid="cta-final">
            Demander mon estimation maintenant
          </Button>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      {showStickyCTA && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-0 inset-x-0 z-50 border-t bg-white/90 backdrop-blur px-4 py-3 lg:hidden"
          data-testid="sticky-cta"
        >
          <Button
            size="lg"
            className="w-full"
            onClick={() => setFormOpen(true)}
          >
            Recevoir mon estimation
          </Button>
        </motion.div>
      )}

      {/* Lead Form */}
      <LeadFormDrawer open={formOpen} onOpenChange={setFormOpen} agentSlug={agentSlug} />
    </div>
  );
};

export default LandingPage;
